/**
 * Return a matching HTML element when the selector resolves to one.
 *
 * @param {ParentNode} root
 * @param {string} selector
 * @returns {HTMLElement | null}
 */
function findHTMLElement(root, selector) {
  const candidate = root.querySelector(selector);
  return candidate instanceof HTMLElement ? candidate : null;
}

/**
 * Return the header in a Shopify section, including when the section itself is
 * the header element.
 *
 * @param {ParentNode} root
 * @returns {HTMLElement | null}
 */
function findHeader(root) {
  if (root instanceof HTMLElement && root.matches('[data-site-header]')) return root;
  return findHTMLElement(root, '[data-site-header]');
}

/** @type {WeakMap<HTMLElement, AbortController>} */
const headerControllers = new WeakMap();

/** @param {HTMLElement} header */
function initializeHeader(header) {
  if (headerControllers.has(header)) return;

  const controller = new AbortController();
  const { signal } = controller;
  const toggleCandidate = header.querySelector('[data-menu-toggle]');
  const toggle = toggleCandidate instanceof HTMLElement ? toggleCandidate : null;
  const drawer = findHTMLElement(header, '[data-menu-drawer]');
  const openIcon = toggle ? findHTMLElement(toggle, '.site-header__menu-toggle-open') : null;
  const closeIcon = toggle ? findHTMLElement(toggle, '.site-header__menu-toggle-close') : null;

  /** @type {NodeListOf<Element>} */
  const disclosures = header.querySelectorAll('[data-nav-disclosure]');

  /** @param {boolean} open */
  function setDrawerOpen(open) {
    if (!toggle || !drawer) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    drawer.hidden = !open;
    if (openIcon) openIcon.hidden = open;
    if (closeIcon) closeIcon.hidden = !open;
    if (open) closeAllDisclosures();
  }

  /** @param {Element | null} [except=null] */
  function closeAllDisclosures(except) {
    disclosures.forEach((btn) => {
      if (except && btn === except) return;
      btn.setAttribute('aria-expanded', 'false');
      const panel = findDisclosurePanel(btn);
      if (panel) panel.hidden = true;
    });
  }

  /**
   * @param {Element} button
   * @returns {HTMLElement | null}
   */
  function findDisclosurePanel(button) {
    const panelId = button.getAttribute('aria-controls');
    return panelId ? findHTMLElement(header, `#${CSS.escape(panelId)}`) : null;
  }

  /**
   * @param {Element} btn
   * @param {boolean} open
   */
  function setDisclosure(btn, open) {
    const panel = findDisclosurePanel(btn);
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.hidden = !open;
  }

  toggle?.addEventListener(
    'click',
    () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      setDrawerOpen(open);
    },
    { signal },
  );

  disclosures.forEach((btn) => {
    btn.addEventListener(
      'click',
      () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        closeAllDisclosures(btn);
        setDisclosure(btn, open);
        if (open) setDrawerOpen(false);
      },
      { signal },
    );
  });

  // Close navigation before native same-page anchor scrolling calculates its target.
  header.addEventListener(
    'click',
    (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest('a[href]');
      if (!(link instanceof HTMLAnchorElement) || link.target === '_blank' || link.hasAttribute('download')) return;
      const destination = new URL(link.href);
      if (destination.origin !== location.origin || destination.pathname !== location.pathname) return;
      if (destination.search !== location.search || destination.hash.length < 2) return;
      setDrawerOpen(false);
      closeAllDisclosures();
    },
    { signal },
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;

      const activeElement = document.activeElement;
      const drawerHasFocus = drawer?.contains(activeElement) ?? false;
      const disclosureToRestore = Array.from(disclosures).find((btn) => {
        if (btn.getAttribute('aria-expanded') !== 'true') return false;
        const panel = findDisclosurePanel(btn);
        return panel?.contains(activeElement) ?? false;
      });

      setDrawerOpen(false);
      closeAllDisclosures();

      if (drawerHasFocus) {
        toggle?.focus();
      } else if (disclosureToRestore instanceof HTMLElement) {
        disclosureToRestore.focus();
      }
    },
    { signal },
  );

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (header.contains(event.target)) return;
      closeAllDisclosures();
    },
    { signal },
  );

  headerControllers.set(header, controller);
}

/** @param {HTMLElement} header */
function destroyHeader(header) {
  headerControllers.get(header)?.abort();
  headerControllers.delete(header);
}

const initialHeader = findHeader(document);
if (initialHeader) initializeHeader(initialHeader);

// Shopify re-renders section markup in place in the theme editor without
// re-running page-load scripts. Keep these document listeners alive so a
// replaced header receives fresh component listeners and removed instances are
// cleaned up.
document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof Element)) return;
  const header = findHeader(event.target);
  if (header) destroyHeader(header);
});

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof Element)) return;
  const header = findHeader(event.target);
  if (header) initializeHeader(header);
});
